import { gql } from '@apollo/client'

export const USER_CHANGES_FRAGMENT = gql`
  fragment UserChangesFields on UserInfoChanges {
    firstname
    lastname
    lastnameMaternal
    phoneNumber
    email
    profilePicture {
      id
      url
      name
    }
    description
  }
`

export const USER_FRAGMENT = gql`
  fragment CoreUserFields on User {
    id
    isAdmin
    adminRole
    financeAccess

    bankTransferOnly
    firstname
    lastname
    lastnameMaternal
    rfc
    constanciaFiscal {
      id
      url
      name
    }
    regimenFiscal
    civilStatus
    usoCFDI
    cfdiFormaPago
    cfdiMetodoPago
    birthdate
    address {
      street1
      street2
      neighborhood
      colonia
      city
      state
      zipcode
      country
    }
    lastKnownLocation {
      city
      state
      center {
        lat
        lng
      }
      updatedAt
    }
    email
    phoneNumber
    description
    languages
    facebookUrl
    instagramUrl
    twitterUrl
    interest
    customerType
    hostType
    hubspotId
    hsOwnerId
    job
    currentCity
    hobbie
    movieAsLife
    favoriteSong

    identification {
      id
      url
      name
    }
    profilePicture {
      id
      url
      name
    }

    bussinessProfile {
      name
      description
      profilePicture {
        id
        url
        name
        width
        height
      }
    }

    isVerified
    accountVerificationError
    requestedVerification
    requestedVerificationAt
    isDeactivated

    aiVerification {
      verdict
      score
      reasons
      reasoning
      idDocumentType
      idDocumentTypeConfidence
      idDocumentAuthentic
      idAuthenticConfidence
      correctedNames {
        firstname
        lastname
        lastnameMaternal
      }
      nameMatches
      birthdateMatches
      extractedBirthdate
      idExpired
      faceMatches
      ranAt
      model
      promptVersion
      autoAction {
        tier
        reasonKeys
        botIdContributed
        appliedAt
      }
    }

    kycBotIdSignal {
      isBot
      surface
      collectedAt
    }

    createdOn
    lastUpdatedOn
    lastVerifiedOn

    rfc
    curp
    clabe

    fiscalCompliance {
      hostType
      isTeam
      highRetention
      retentionIvaRate
      retentionIsrRate
      missingFields
      isComplete
      completeProfileUrl
      shouldShowFiscalWarning
      isOffPlatform
      stampRejected
      stampRejectedAt
      stampRejectedFields
    }
  }
`

export const TEAM_FRAGMENT = gql`
  fragment TeamFields on Team {
    id
    lastUpdatedOn
    createdOn

    name
    description
    logo {
      id
      url
      name
    }
    icon {
      id
      url
      name
    }

    legalName
    legalRepName
    legalRepEmail
    legalRepTitle
    rfc
    email
    phone
    bankTransferOnly
    collectionMode
    segment
    constanciaFiscal {
      id
      url
      name
    }

    address {
      street1
      street2
      colonia
      city
      state
      zipcode
      neighborhood
    }

    clabe
    regimenFiscal
    usoCFDI
    cfdiFormaPago
    cfdiMetodoPago
    partnerType
    segment
    commissionType
    commissionValue

    fiscalCompliance {
      hostType
      isTeam
      highRetention
      retentionIvaRate
      retentionIsrRate
      missingFields
      isComplete
      completeProfileUrl
      shouldShowFiscalWarning
      isOffPlatform
      stampRejected
      stampRejectedAt
      stampRejectedFields
    }

    members {
      userId
      addedOn
      role
      topics
      locationIds

      user {
        id
        isAdmin
        email
        firstname
        lastname
        profilePicture {
          id
          url
        }
      }
    }
  }
`

export const LOC_CONFIGURATION_FRAGMENT = gql`
  fragment LocConfigurationFields on Configuration {
    nanoId
    id
    lastUpdatedOn
    createdOn
    isDeactivated
    isDeleted

    quantity
    availability
    watchingCount

    price
    pricing {
      impuestos
      insurance
      tde
      tdera
      tdp
      tds
      tf
    }
    dynamicPricing
    minPrice
    # suggestedPrice
    discountEnabled

    dealType
    resolvedDealType

    size {
      width
      length
      height
    }
    canStoreVehicle
    locationType
    features
    pillarPage
    urlRoute

    coverPhotoId
    minMonths
    specialDiscountId
    specialDiscount {
      id
      description
      discountMonths
      expiresAt
      minMonths
    }

    oneTimeFees {
      amount
      category
      description
      withTax
    }
  }
`

/**
 * Minimal card-rendering fragment. Trimmed vs. the original:
 *   photos[].id               → only .url is used on cards
 *   defaultConfiguration:
 *     - canStoreVehicle       → only AddPostFlow / Checkout reads it
 *     - pillarPage            → only Details page / pixels read it
 *   configurations[]:
 *     - locationType          → only defaultConfiguration's variant is read
 *     - canStoreVehicle       → unused on cards
 *     - discountEnabled       → unused on cards (getDiscount uses defaultConfig)
 *     - pillarPage            → unused on cards
 *
 * configurations[].urlRoute IS kept — LocationSearchCard looks it up when a
 * parent passes a configurationId prop (see pages/directory/listings/[page]
 * which flatMaps configurations and passes configuration.id per card).
 *
 * Consumers: pillar/city/hood SSG, SearchManager client query, home
 * RecommendedSection, LandingStorages StorageSection, Details
 * SimilarLocations, business/team/user/directory listings pages.
 */
export const CARD_LOCATION_FIELDS = gql`
  fragment CardLocationFields on Location {
    id
    urlRoute
    # Server-decided 45-day inventory freshness — gates off-platform card
    # scarcity (isAvailabilityClaimable); never a client date check (#418).
    inventoryIsFresh
    respondsWithin24h
    # Branch-level distinct inquirers, 30 days (null below 3) — the
    # «N consultas en 30 días» pill (getCardDemand).
    recentInquiries
    reviewScore
    reviewCount
    isVerified
    coordinates {
      lat
      lng
    }
    shortAddress
    # Search-card label. Prefer this over shortAddress when rendering a card —
    # it applies the admin branch reference and normalizes the municipality.
    cardLocation
    currentCurrency
    photos {
      url
    }
    # searchInput makes the server return the pillar/search-matched config's
    # own cover (when set and a genuinely different image) instead of the
    # location-level one — so a parking+storage location leads its parking
    # card with the parking photo. Same config selection as
    # defaultConfiguration below, resolved server-side so cards don't need
    # photos[].id/name in the payload.
    # coverPhoto.id feeds the search_result_view/click cover_photo_id
    # property — the cover-photo bandit's per-arm CTR (spec 2026-09-23).
    coverPhoto(searchInput: $searchInput) {
      id
      url
    }
    defaultConfiguration(searchInput: $searchInput) {
      id
      size {
        width
        length
      }
      locationType
      price
      # Server-resolved deal type. Without it every card-side
      # getEffectiveDealType fell back to price/pillar inference and read a
      # Traditional card as SpotMe Now (discount pill, scarcity gate, the
      # search_result_* deal_type prop).
      resolvedDealType
      discountEnabled
      availability
      urlRoute
      coverPhotoId
      specialDiscount {
        id
        description
        discountMonths
        expiresAt
        minMonths
      }
      # The Traditional operator's own promotion → card pill (server-gated).
      operatorPromo {
        kind
        headline
        promoPrice
        percentOff
        isUpTo
        freeMonths
        tiers {
          minMonths
          percentOff
        }
      }
    }
    # Precomputed multi-config summary instead of the raw configurations[]
    # array — the card only needs the active count, size range, and the two
    # "cheapest" prices/sizes, all of which useConfigSummary now reads from
    # here. Drops ~30-40 kB / search page off __NEXT_DATA__. Backend-computed
    # by Location.configSummary (mirrors the hook). See
    # docs/performance/large-page-data.md.
    configSummary(searchInput: $searchInput) {
      activeCount
      minSizeM2
      maxSizeM2
      cheapestPrice
      cheapestSizeM2
      cheapestPerSqmPrice
      cheapestPerSqmSizeM2
      totalAvailability
    }
  }
`

export const ALL_LOCATION_FIELDS = gql`
  ${USER_FRAGMENT}
  ${LOC_CONFIGURATION_FRAGMENT}

  fragment AllLocationFields on Location {
    id
    urlRoute
    googleReviewUrl
    # Server-decided 45-day inventory freshness — gates off-platform
    # listing-page scarcity (isAvailabilityClaimable).
    inventoryIsFresh
    respondsWithin24h
    # Branch-level distinct inquirers, 30 days (null below 3) — Header's
    # «N personas preguntaron…» pill.
    recentInquiries

    address
    shortAddress
    # Public-facing location label — keep every renter-visible surface (search
    # card, map popup, listing header) on this, not shortAddress, or the same
    # listing reads differently depending on where you look.
    cardLocation
    bankTransferOnly
    # True when the host TEAM collects rent directly — the funnel skips the
    # payment + insurance steps. Boolean only; the CLABE is never public.
    directCollection
    # Operator confirmed its own price already includes IVA (Traditional card
    # says "IVA incluido" instead of "+ IVA = $X").
    listPriceIncludesIva
    addressDetails {
      street1
      street2
      neighborhood
      colonia
      city
      state
      zipcode
      country
    }
    coordinates {
      lat
      lng
    }
    publicAddress

    title
    description
    privateSpace
    indoorOrOutdoor
    currency
    currentCurrency
    photos {
      id
      url
      name
      width
      height
    }
    coverPhoto {
      id
      url
      name
      width
      height
    }
    welcomeMessage
    welcomePhotos {
      id
      url
      name
      width
      height
    }
    accessPermission
    accessFrequency
    accessHours
    accessAvailability
    agreedToTerms
    useOfSpace
    propertyType
    logisticsServices

    defaultConfigurationId
    defaultConfiguration {
      ...LocConfigurationFields
    }
    configurations {
      ...LocConfigurationFields
    }

    submittedBy
    submittedOn
    lastUpdatedOn

    isDeactivated
    isDraft
    isDeleted

    requestedVerification
    requestedVerificationAt
    isVerified
    reasonForDenial

    user {
      ...CoreUserFields
    }
    bussinessProfile {
      name
      description
      profilePicture {
        id
        url
        name
        width
        height
      }
    }
    teamId

    team {
      id
      name
      description
      icon {
        id
        url
        name
      }
    }

    visualScore
    reviewScore
    reviewCount

    hasTechnicalReport
  }
`

/**
 * Lightweight checkout fragment for list/table views.
 * Only includes fields used by ReservationTable, CheckoutCard, events, and payments.
 */
export const CHECKOUT_LIST_FRAGMENT = gql`
  fragment CheckoutListFields on Checkout {
    id
    locationId
    renterId
    landlordId
    price
    quantity
    completedAt
    requestedStartDate
    startedAt
    cancelledAt
    endedAt
    hasEnded
    isLandlordApproved
    landlordAssessedAt
    expiredAt
    scheduledEndDate
    refundAmount
    needsInvoice
    hasMissingFactura

    # Unpaid episode — just enough for <UnpaidBadge /> on the list rows and
    # the status sub-line. The full panel refetches the rest on the detail
    # page; keeping this slice small matters because the fragment is the
    # LIST query (20 rows a page).
    unpaidSince
    unpaidAmount
    unpaidInvoiceCount
    autoReleasedAt
    rescissionNoticeSentAt
    rescissionEffectiveAt
    rescissionCuredAt

    pricing {
      tdera
    }

    renter {
      id
      firstname
      lastname
      profilePicture {
        id
        url
      }
    }

    renterTeam {
      id
      name
      icon {
        id
        url
      }
    }

    # Renter-side lists label the HOST, so the row needs the landlord team AND
    # the individual landlord (for listings that belong to no team).
    landlord {
      id
      firstname
      lastname
      profilePicture {
        id
        url
      }
    }

    landlordTeam {
      id
      name
      icon {
        id
        url
      }
    }

    location {
      id
      shortAddress
      currentCurrency
      coverPhoto {
        id
        url
      }
      photos {
        id
        url
      }
      team {
        id
        name
        icon {
          id
          url
        }
      }
      defaultConfigurationId
      defaultConfiguration {
        id
        locationType
        canStoreVehicle
        price
        coverPhotoId
        size {
          width
          length
        }
      }
    }
  }
`

/**
 * Lightweight location fragment for the listings list/table page.
 * Only includes fields used by ListingsTable and LocationCard.
 */
export const LOCATION_LIST_FRAGMENT = gql`
  fragment LocationListFields on Location {
    id
    urlRoute
    shortAddress
    currentCurrency

    coverPhoto {
      id
      url
    }
    photos {
      id
      url
    }

    isDraft
    isDeactivated
    isVerified
    reasonForDenial

    teamId
    team {
      id
      name
    }
    # Individual-host fallback for the listings table's host column: a listing
    # with no team is one person's. Team still wins when both exist.
    user {
      id
      firstname
      lastname
    }

    defaultConfigurationId
    defaultConfiguration {
      id
      locationType
      canStoreVehicle
      price
      dealType
      resolvedDealType
      coverPhotoId
      size {
        width
        length
      }
    }

    configurations {
      id
      # locationType is what the insurance vehicle-guard reads, and it must come
      # from the checkout's OWN configuration — a mixed listing can hold both
      # storage and parking units, so defaultConfiguration is the wrong answer
      # (see src/utils/insuranceEligibility.ts).
      #
      # Do NOT prune this to save payload. Without it, isInsurableCheckout still
      # MATCHES the config by id, reads an undefined locationType, and returns
      # TRUE — i.e. it fails open and silently declares every space insurable,
      # parking included. That is how the nextStep routing in items.tsx came to
      # send vehicle-space renters to the Insurance step anyway, leaving the
      # whole parking block resting on the serverSideProps bounce alone
      # (found 2026-09-01).
      locationType
    }
  }
`

export const CHECKOUT_COMMISSION_FRAGMENT = gql`
  fragment CheckoutCommissionFields on Checkout {
    commissionBreakdown {
      commissionPercent
      isOverride
      spotmeAmount
      spotmeAmountTotal
      stripeFeeAmount
      stripeFeeAmountTotal
      hostAmount
      hostAmountTotal
      transferPercent
      renterTotal
    }
  }
`

export const CHECKOUT_FRAGMENT = gql`
  ${USER_FRAGMENT}
  ${ALL_LOCATION_FIELDS}
  ${TEAM_FRAGMENT}

  fragment CoreCheckoutFields on Checkout {
    id
    locationId
    configurationId
    renterId
    landlordId
    price
    currency
    quantity
    pricing {
      impuestos
      insurance
      tde
      tdera
      tdp
      tds
      tf
    }
    storageTime
    storageItems
    storageNeeds
    storageItemsPhotos {
      id
      url
      name
    }
    insuranceType
    insurancePremium
    vehicles {
      color
      licensePlate
      make
      model
      type
      year
    }

    stripeSessionCreatedAt
    stripeSessionId
    stripePaymentMethodId
    stripePaymentMethod {
      id
      type
      card {
        brand
        last4
        funding
      }
    }
    stripeCheckoutSessionUrl
    stripeSubscriptionId
    stripePaymentMethodCompletedAt
    stripeBankTransfer

    # Direct collection ("SpotMe Now, cobro directo"). All three are null on a
    # normal platform-billed rental: collectionMode is stamped at approval, and
    # the instructions + notice ledger are participant-or-admin gated server-side.
    collectionMode
    # The first period billed directly. Between migration and the cutover a
    # rental is already HOST_DIRECT while SpotMe still collects the current
    # month, so the payment card needs this to avoid telling the renter to
    # transfer for a month their card was already charged for.
    directSince
    directPaymentInstructions {
      clabe
      beneficiary
      rfc
      # Some MX banks demand the beneficiary's address to register a new SPEI
      # destination — without these the renter cannot complete the transfer.
      beneficiaryAddress
      beneficiaryCity
      beneficiaryState
      beneficiaryZipcode
    }
    directNotices {
      kind
      period
      sentAt
      amount
    }

    isLandlordApproved
    landlordNotes
    landlordPhotos {
      id
      url
      name
      width
      height
    }
    rejectionReason
    endedReason
    verifiedBy
    verifiedByUser {
      id
      firstname
      profilePicture {
        url
      }
    }

    requestedStartDate
    createdAt
    completedAt
    startedAt
    landlordAssessedAt
    expiredAt
    cancelledAt
    cancelledBy
    cancelledByUserId
    cancelledByUser {
      id
      firstname
      profilePicture {
        url
      }
    }
    endedAt
    hasEnded
    scheduledEndDate
    cancellationRequestedAt
    cancellationInitiatedBy
    finalMonthProratedAmount
    refundAmount
    renterClabe

    renter {
      ...CoreUserFields
    }
    landlord {
      ...CoreUserFields
    }

    renterTeamId
    renterTeam {
      ...TeamFields
    }
    landlordTeamId
    landlordTeam {
      ...TeamFields
    }

    location {
      ...AllLocationFields
    }

    needsInvoice
    missingFacturaPeriods
    missingFacturas {
      period
      invoiceId
      conceptKey
      amount {
        subtotal
        iva
        total
      }
      isProrated
      paidAt
    }
    hasMissingFactura
    chatId

    commissionOverride

    lastPaymentSatInfo {
      formaDePagoCode
      formaDePagoLabel
      metodoDePago
    }

    oneTimeFees {
      amount
      category
      description
      withTax
    }
  }
`

export const MESSAGE_FRAGMENT = gql`
  fragment AllMessageFields on Message {
    id
    chatId
    timestamp
    readReceipts {
      participant {
        id
        type
      }
      read
    }
    message
    files {
      id
      url
      name
    }
    event {
      eventType
      date
    }
    author {
      id
      type
      team {
        id
      }
      user {
        id
      }
    }
    authorUser {
      id
      firstname
      lastname
    }
    checkout {
      id
      locationId
      configurationId
      renterId
      landlordId
      landlordTeamId
      price
      quantity
      pricing {
        tfNoInsurance
        insurance
      }
      storageNeeds
      requestedStartDate
      completedAt
      startedAt
      cancelledAt
      scheduledEndDate
      isLandlordApproved
      landlordAssessedAt
      expiredAt
      hasEnded
      location {
        id
        urlRoute
        configurations {
          id
          locationType
          canStoreVehicle
          size {
            width
            length
            height
          }
        }
        photos {
          id
          url
        }
        coverPhoto {
          id
          url
        }
        shortAddress

        defaultConfigurationId
        defaultConfiguration {
          id
          size {
            width
            length
            height
          }
          locationType
          price
          minPrice
          pricing {
            tde
            tds
          }
          discountEnabled
          canStoreVehicle
          pillarPage
          urlRoute
          coverPhotoId
          specialDiscount {
            id
            description
            discountMonths
            expiresAt
            minMonths
          }
        }
      }
    }
    locations {
      id
      urlRoute
      coverPhoto {
        id
        url
      }
      photos {
        id
        url
      }
      shortAddress
      defaultConfigurationId
      defaultConfiguration {
        id
        size {
          width
          length
          height
        }
        locationType
        price
        minPrice
        pricing {
          tde
          tds
        }
        discountEnabled
        canStoreVehicle
        pillarPage
        urlRoute
        coverPhotoId
        specialDiscount {
          id
          description
          discountMonths
          expiresAt
          minMonths
        }
      }
    }
    reported
  }
`

export const ALL_CHAT_FIELDS = gql`
  ${MESSAGE_FRAGMENT}

  fragment AllChatFields on Chat {
    id
    lastMessage {
      message
      files {
        id
        url
      }
    }
    lastMessageTimestamp
    participants {
      id
      type
      team {
        id
        name
        icon {
          id
          url
        }
      }
      user {
        id
        isVerified
        firstname
        lastname
        profilePicture {
          id
          url
        }
        userChanges {
          firstname
          lastname
          profilePicture {
            id
            url
          }
        }
      }
    }
    unreadMessages
    messages(pagination: $pagination) {
      total
      messages {
        ...AllMessageFields
      }
    }

    currentParticipant {
      id
      type
    }
  }
`

export const CARD_CHAT_FIELDS = gql`
  fragment CardChatFields on Chat {
    id
    participants {
      id
      type
      team {
        id
        name
        icon {
          id
          url
        }
      }
      user {
        id
        isVerified
        firstname
        lastname
        profilePicture {
          id
          url
        }
        userChanges {
          firstname
          lastname
          profilePicture {
            id
            url
          }
        }
      }
    }
    lastMessage {
      message
      files {
        id
        url
      }
    }
    lastMessageTimestamp
    unreadMessages

    # Which space the thread is about (server Chat.locationLabel: a constant
    # 3 batched reads per page). Label fields only, never a full Location.
    locationLabel {
      id
      title
      area
      configuration {
        id
        locationType
        canStoreVehicle
        size {
          length
          width
        }
      }
    }

    # Presence-only. The thread list uses it to decide whether the renter has
    # booked with this host yet — before that, the landlord stays anonymous
    # (see the anonymize flag on getChatProfile). Do NOT widen this
    # selection: the list has no use for checkout detail, and every field
    # here is fetched once per thread.
    checkout {
      id
    }

    currentParticipant {
      id
      type
    }
  }
`

export const ALL_REVIEW_FIELDS = gql`
  ${USER_FRAGMENT}

  fragment AllReviewFields on Review {
    interest
    rating
    review
    createdAt

    response
    respondedAt

    reviewerId
    reviewer {
      ...CoreUserFields
    }
  }
`
